/**
 * URL <-> typed filter helpers for the admin pages (pure, testable).
 *
 * A schema is a plain object of field specs built with the helpers below.
 * `parseFilters` reads `URLSearchParams` into a typed object (falling back to
 * defaults), `serializeFilters` writes it back while omitting defaults so URLs
 * stay short and shareable.
 */

export type StringSpec = { kind: 'string'; default: string; maxLength?: number };
export type NumberSpec = { kind: 'number'; default: number; min?: number; max?: number };
export type BooleanSpec = { kind: 'boolean'; default: boolean };
export type EnumSpec<V extends string = string> = { kind: 'enum'; default: V; values: readonly V[] };

export type FilterSpec = StringSpec | NumberSpec | BooleanSpec | EnumSpec<string>;

export type FilterSchema = Record<string, FilterSpec>;

export type FilterValues<S extends FilterSchema> = { [K in keyof S]: S[K]['default'] };

export function stringField(def = '', opts: { maxLength?: number } = {}): StringSpec {
  return { kind: 'string', default: def, ...opts };
}

export function numberField(def: number, opts: { min?: number; max?: number } = {}): NumberSpec {
  return { kind: 'number', default: def, ...opts };
}

export function booleanField(def = false): BooleanSpec {
  return { kind: 'boolean', default: def };
}

export function enumField<const V extends string>(values: readonly V[], def: V): EnumSpec<V> {
  return { kind: 'enum', default: def, values };
}

export function pageField(): NumberSpec {
  return numberField(1, { min: 1 });
}

function parseOne(spec: FilterSpec, raw: string | null): string | number | boolean {
  if (raw == null) return spec.default;
  switch (spec.kind) {
    case 'string': {
      const v = raw.trim();
      if (!v) return spec.default;
      return spec.maxLength ? v.slice(0, spec.maxLength) : v;
    }
    case 'number': {
      const n = Number(raw);
      if (!Number.isFinite(n)) return spec.default;
      let v = Math.trunc(n);
      if (spec.min != null && v < spec.min) v = spec.min;
      if (spec.max != null && v > spec.max) v = spec.max;
      return v;
    }
    case 'boolean': {
      const v = raw.trim().toLowerCase();
      if (v === '1' || v === 'true' || v === 'yes') return true;
      if (v === '0' || v === 'false' || v === 'no') return false;
      return spec.default;
    }
    case 'enum':
      return (spec.values as readonly string[]).includes(raw) ? raw : spec.default;
  }
}

function toSearchParams(input: URLSearchParams | string | Record<string, string> | undefined): URLSearchParams {
  if (!input) return new URLSearchParams();
  if (input instanceof URLSearchParams) return input;
  return new URLSearchParams(input);
}

export function parseFilters<S extends FilterSchema>(
  input: URLSearchParams | string | Record<string, string> | undefined,
  schema: S
): FilterValues<S> {
  const params = toSearchParams(input);
  const out: Record<string, string | number | boolean> = {};
  for (const key of Object.keys(schema)) {
    out[key] = parseOne(schema[key], params.get(key));
  }
  return out as FilterValues<S>;
}

/**
 * Serialise typed values back to search params. Keys equal to their default
 * (or empty) are removed; params not in the schema (e.g. `tab`) are preserved
 * when `base` is given.
 */
export function serializeFilters<S extends FilterSchema>(
  values: Partial<FilterValues<S>>,
  schema: S,
  base?: URLSearchParams
): URLSearchParams {
  const params = new URLSearchParams(base ? base.toString() : '');
  for (const key of Object.keys(schema)) {
    const spec = schema[key];
    const value = (values as Record<string, unknown>)[key];
    if (value === undefined || value === null || value === spec.default || value === '') {
      params.delete(key);
      continue;
    }
    if (spec.kind === 'boolean') {
      params.set(key, value ? '1' : '0');
    } else {
      params.set(key, String(value));
    }
  }
  return params;
}

export type DatePreset = '1h' | '6h' | '24h' | '7d' | '30d' | '90d';

const DATE_PRESET_HOURS: Record<DatePreset, number> = {
  '1h': 1,
  '6h': 6,
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
  '90d': 24 * 90,
};

export function datePresetToHours(preset: DatePreset | string, fallback = 24): number {
  return (DATE_PRESET_HOURS as Record<string, number>)[preset] ?? fallback;
}

export const HOURS_PRESETS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: '1h' },
  { value: 6, label: '6h' },
  { value: 24, label: '24h' },
  { value: 168, label: '7d' },
];

export const DAYS_PRESETS = [7, 30, 90] as const;
export type DaysPreset = (typeof DAYS_PRESETS)[number];

/** Coerce any input to one of the allowed day presets (default 30). */
export function daysPreset(raw: string | number | null | undefined, fallback: DaysPreset = 30): DaysPreset {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return (DAYS_PRESETS as readonly number[]).includes(n) ? (n as DaysPreset) : fallback;
}

export const WEEKS_PRESETS = [4, 12, 26] as const;
export type WeeksPreset = (typeof WEEKS_PRESETS)[number];

export function weeksPreset(raw: string | number | null | undefined, fallback: WeeksPreset = 12): WeeksPreset {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return (WEEKS_PRESETS as readonly number[]).includes(n) ? (n as WeeksPreset) : fallback;
}

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV4_CIDR_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}\/(3[0-2]|[12]?\d)$/;
// Loose IPv6: hex groups separated by ':' with at most one '::'; optional embedded IPv4 tail.
const IPV6_LOOSE_RE = /^(?=.*:)(?!.*:::)[0-9a-f:]{2,39}(\.\d{1,3}){0,3}$/i;

/** Loose validation for IPv4, IPv4 CIDR and IPv6 literals. */
export function isValidIp(value: string | null | undefined): boolean {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;
  if (IPV4_RE.test(v) || IPV4_CIDR_RE.test(v)) return true;
  if (!IPV6_LOOSE_RE.test(v)) return false;
  const doubleColons = v.split('::').length - 1;
  if (doubleColons > 1) return false;
  const groups = v.split(':').filter(Boolean);
  if (doubleColons === 0 && groups.length !== 8) return false;
  if (doubleColons === 1 && groups.length > 7) return false;
  return groups.every((g) => /^[0-9a-f]{1,4}$/i.test(g) || /^\d{1,3}(\.\d{1,3}){3}$/.test(g));
}

export type ExpiryPreset = '1h' | '24h' | '7d' | '30d' | 'never';

export const EXPIRY_PRESETS: ReadonlyArray<{ value: ExpiryPreset; label: string }> = [
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'never', label: 'Never' },
];

export function expiryPresetToHours(preset: ExpiryPreset | string): number | null {
  switch (preset) {
    case '1h':
      return 1;
    case '24h':
      return 24;
    case '7d':
      return 24 * 7;
    case '30d':
      return 24 * 30;
    case 'never':
      return null;
    default:
      return 24;
  }
}
